import { useEffect, useState, useCallback } from "react";
import { Loader2, Save, RefreshCw, Stamp, UploadCloud, QrCode } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loading } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SHAPES = [
  { v: "square", label: "四角印" },
  { v: "round", label: "丸印" },
  { v: "none", label: "印なし" },
];
const CONTENTS = [
  { v: "rep", label: "代表者名＋印（例：黒崎印）" },
  { v: "company", label: "会社名（例：ウェルフィ会印）" },
  { v: "custom", label: "自由入力" },
];
const PRESET_ROLES = ["代表者", "代表取締役", "代表取締役社長", "代表理事", "理事長", "会長", "施設長", "管理者"];

export default function PlatformCertificate() {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [customRole, setCustomRole] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const onRoleChange = (v) => {
    if (v === "__custom__") { setCustomRole(true); set("issuer_role", ""); }
    else { setCustomRole(false); set("issuer_role", v); }
  };

  useEffect(() => {
    api.get("/platform/certificate-settings").then(({ data }) => {
      setF(data);
      setCustomRole(!!data.issuer_role && !PRESET_ROLES.includes(data.issuer_role));
    });
  }, []);

  const refreshPreview = useCallback(async (payload) => {
    setPreviewing(true);
    try {
      const { data } = await api.post("/platform/certificate-preview", payload, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      setPreview((old) => { if (old) URL.revokeObjectURL(old); return url; });
    } catch (e) { toast.error(apiErr(e)); } finally { setPreviewing(false); }
  }, []);

  useEffect(() => { if (f) refreshPreview(f); /* eslint-disable-next-line */ }, [f !== null]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/platform/certificate-settings", f);
      toast.success("保存しました");
      refreshPreview(f);
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const uploadSeal = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/platform/upload", fd);
      setF((p) => ({ ...p, seal_image: data.url, seal_use_image: true }));
      toast.success("印影画像をアップロードしました");
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!f) return <Loading />;

  return (
    <div data-testid="platform-certificate">
      <div className="flex items-end justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white flex items-center gap-2"><Stamp className="w-6 h-6 text-amber-400" />修了証設定</h1>
          <p className="text-sm text-slate-400 mt-1">修了証明書の文言・発行者の肩書き・角印の様式を設定します（全法人共通）</p>
        </div>
        <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900" onClick={save} disabled={saving} data-testid="cert-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}保存
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-4">
          <div>
            <Label className="text-slate-300">タイトル</Label>
            <Input value={f.title} onChange={(e) => set("title", e.target.value)} className="mt-1 bg-slate-800 border-slate-700 text-slate-100" data-testid="cert-title" />
          </div>
          <div>
            <Label className="text-slate-300">発行者の肩書き</Label>
            <Select value={customRole ? "__custom__" : (PRESET_ROLES.includes(f.issuer_role) ? f.issuer_role : "__custom__")} onValueChange={onRoleChange}>
              <SelectTrigger className="mt-1 bg-slate-800 border-slate-700 text-slate-100" data-testid="cert-role-select"><SelectValue placeholder="肩書きを選択" /></SelectTrigger>
              <SelectContent>
                {PRESET_ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                <SelectItem value="__custom__">自由入力</SelectItem>
              </SelectContent>
            </Select>
            {(customRole || !PRESET_ROLES.includes(f.issuer_role)) && (
              <Input value={f.issuer_role} onChange={(e) => set("issuer_role", e.target.value)} placeholder="肩書きを入力（例：園長）" className="mt-2 bg-slate-800 border-slate-700 text-slate-100" data-testid="cert-role" />
            )}
          </div>
          <div>
            <Label className="text-slate-300">本文（研修修了証）</Label>
            <Textarea value={f.body_training} onChange={(e) => set("body_training", e.target.value)} rows={2} className="mt-1 bg-slate-800 border-slate-700 text-slate-100" data-testid="cert-body-training" />
          </div>
          <div>
            <Label className="text-slate-300">本文（入社研修 修了証）</Label>
            <Textarea value={f.body_onboarding} onChange={(e) => set("body_onboarding", e.target.value)} rows={2} className="mt-1 bg-slate-800 border-slate-700 text-slate-100" data-testid="cert-body-onboarding" />
          </div>

          <div className="border-t border-slate-800 pt-4">
            <div className="text-sm font-semibold text-slate-200 mb-3">角印の様式</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300">形</Label>
                <Select value={f.seal_shape} onValueChange={(v) => set("seal_shape", v)}>
                  <SelectTrigger className="mt-1 bg-slate-800 border-slate-700 text-slate-100" data-testid="cert-seal-shape"><SelectValue /></SelectTrigger>
                  <SelectContent>{SHAPES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">印字内容</Label>
                <Select value={f.seal_content} onValueChange={(v) => set("seal_content", v)} disabled={f.seal_shape === "none"}>
                  <SelectTrigger className="mt-1 bg-slate-800 border-slate-700 text-slate-100" data-testid="cert-seal-content"><SelectValue /></SelectTrigger>
                  <SelectContent>{CONTENTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {f.seal_content === "custom" && f.seal_shape !== "none" && (
              <div className="mt-3">
                <Label className="text-slate-300">印の文字（自由入力）</Label>
                <Input value={f.seal_label || ""} onChange={(e) => set("seal_label", e.target.value)} placeholder="例：理事長印" className="mt-1 bg-slate-800 border-slate-700 text-slate-100" data-testid="cert-seal-label" />
              </div>
            )}
          </div>

          <div className="border-t border-slate-800 pt-4">
            <div className="text-sm font-semibold text-slate-200 mb-3">印影画像（社判・代表者印）</div>
            <div className="flex items-center gap-3">
              {f.seal_image
                ? <img src={`${BACKEND}${f.seal_image}`} alt="seal" className="w-16 h-16 object-contain bg-white rounded border border-slate-700" data-testid="cert-seal-thumb" />
                : <div className="w-16 h-16 rounded border border-dashed border-slate-700 flex items-center justify-center text-slate-500 text-xs">なし</div>}
              <label className="cursor-pointer">
                <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => uploadSeal(e.target.files?.[0])} data-testid="cert-seal-upload" />
                <span className="inline-flex items-center px-3 py-2 rounded-md bg-slate-800 border border-slate-700 text-slate-200 text-sm hover:bg-slate-700"><UploadCloud className="w-4 h-4 mr-1" />画像をアップロード</span>
              </label>
              {f.seal_image && <button onClick={() => setF((p) => ({ ...p, seal_image: "", seal_use_image: false }))} className="text-slate-400 hover:text-rose-400 text-sm" data-testid="cert-seal-remove">削除</button>}
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-300 mt-3">
              <input type="checkbox" checked={!!f.seal_use_image} disabled={!f.seal_image} onChange={(e) => set("seal_use_image", e.target.checked)} data-testid="cert-seal-use-image" />
              アップロードした画像を角印として使用する（未使用時は上の描画角印）
            </label>
          </div>

          <div className="border-t border-slate-800 pt-4">
            <div className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-1.5"><QrCode className="w-4 h-4 text-amber-400" />真正性（QR・連番透かし）</div>
            <label className="flex items-center gap-2 text-sm text-slate-300">
              <input type="checkbox" checked={f.show_qr !== false} onChange={(e) => set("show_qr", e.target.checked)} data-testid="cert-show-qr" />
              検証用QRコードを表示（スキャンで有効性を確認）
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-300 mt-2">
              <input type="checkbox" checked={f.show_serial !== false} onChange={(e) => set("show_serial", e.target.checked)} data-testid="cert-show-serial" />
              証明書番号の透かしを表示
            </label>
            <div className="mt-3">
              <Label className="text-slate-300 text-xs">検証URLのベース（任意・空欄で自動検出）</Label>
              <Input value={f.verify_base_url || ""} onChange={(e) => set("verify_base_url", e.target.value)} placeholder="https://（自動）" className="mt-1 bg-slate-800 border-slate-700 text-slate-100" data-testid="cert-verify-base" />
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800" onClick={() => refreshPreview(f)} disabled={previewing} data-testid="cert-preview-btn">
              {previewing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}プレビュー更新
            </Button>
          </div>
        </div>

        {/* Preview */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-3">
          <div className="flex items-center justify-between mb-2 px-1">
            <span className="text-xs text-slate-400">プレビュー（サンプルデータ）</span>
            {preview && <a href={preview} target="_blank" rel="noreferrer" className="text-xs text-amber-400 hover:underline" data-testid="cert-preview-newtab">別タブで開く</a>}
          </div>
          {preview ? (
            <iframe title="cert-preview" src={preview} className="w-full rounded-lg bg-white" style={{ height: 720 }} data-testid="cert-preview-frame" />
          ) : (
            <div className="h-[720px] flex items-center justify-center text-slate-500 text-sm">プレビューを生成中…</div>
          )}
        </div>
      </div>
    </div>
  );
}
